// Owner experience phones: the live values a unit page shows for each fictional unit.
// Keyed by the unit key from heroUnits / sectors. Every value here is invented for illustration.
// A key with no entry here still renders: phoneData() builds sensible values from the unit itself.
import type { Sector } from '../variants/c/sectors';

/** One thin callout from the product photo to a live value. at = anchor on the photo, in % of the hero box. */
export type Callout = { k: string; v: string; at?: [number, number] };

export type Live = {
  label: string; // "Heat delivered today"
  value: string; // "38.4"
  unit: string; // "kWh"
  sub: string; // "Efficiency 3.6 · 4.2°C outside"
  series: number[]; // the sparkline, oldest first
};

export type PhoneExtra = {
  status: string; // "Running normally"
  callouts: [Callout, Callout, Callout, Callout]; // top left, top right, bottom left, bottom right
  live?: Live;
  heroPos?: string; // object-position for the catalogue shot inside the phone
  service?: string; // sub-value on the service history row
  parts?: string; // sub-value on the parts row
};

// Default anchor points: where the four callout lines meet the product.
const ANCHORS: [number, number][] = [
  [38, 42],
  [62, 36],
  [41, 70],
  [61, 66],
];

const EXTRA: Partial<Record<string, PhoneExtra>> = {
  heatpump: {
    status: 'Running normally',
    callouts: [
      { k: 'Warranty', v: 'To Mar 2033' },
      { k: 'Last service', v: '12 Mar' },
      { k: 'Filter', v: 'Due in 3 weeks' },
      { k: 'Firmware', v: 'Up to date' },
    ],
    live: {
      label: 'Heat today',
      value: '38.4',
      unit: 'kWh',
      sub: 'Efficiency 3.6 · 4°C outside',
      series: [2.1, 2.0, 1.9, 2.2, 2.6, 3.4, 3.9, 3.6, 2.8, 2.2, 1.9, 1.7, 1.6, 1.7, 1.9, 2.3, 2.9, 3.5, 3.8, 3.3],
    },
    service: '2 visits · last 12 Mar',
  },
  oven: {
    status: 'Ready · 180°C combi',
    callouts: [
      { k: 'Warranty', v: 'To Apr 2028' },
      { k: 'Last service', v: '8 Apr' },
      { k: 'Descale', v: 'Due in 40 h' },
      { k: 'Core probe', v: 'Calibrated' },
    ],
    live: {
      label: 'Cycles today',
      value: '14',
      unit: 'cycles',
      sub: '9 h 20 min running',
      series: [0, 0, 1, 2, 2, 3, 2, 1, 1, 0.6, 1.2, 2.4, 3, 2.6, 1.4, 0.8, 1.8, 2.8, 3.2, 2.2],
    },
    service: '3 visits · last 8 Apr',
  },
  mower: {
    status: 'Parked · charged',
    callouts: [
      { k: 'Blades', v: 'Sharp' },
      { k: 'Battery', v: '12.6 V' },
      { k: 'Service', v: 'Due in 20 hours' },
      { k: 'Owners', v: '1' },
    ],
    live: {
      label: 'Mowing this week',
      value: '3.4',
      unit: 'hours',
      sub: '186 hours on the meter',
      series: [0, 0.6, 0, 0, 1.1, 0, 0, 0.4, 0.9, 0, 0, 0.7, 0, 1.2, 0, 0, 0.5, 0.8, 0, 1.0],
    },
    service: '2 services · last at 160 h',
  },
  ebike: {
    status: 'Locked · 94% battery health',
    callouts: [
      { k: 'Battery health', v: '94%' },
      { k: 'Range', v: '82 km' },
      { k: 'Frame no.', v: 'Registered' },
      { k: 'Service', v: 'Due in 300 km' },
    ],
    live: {
      label: 'Ridden this week',
      value: '64',
      unit: 'km',
      sub: 'Assist mostly eco · 2 owners',
      series: [0, 8, 0, 12, 0, 0, 9, 14, 0, 6, 0, 11, 13, 0, 0, 7, 10, 0, 15, 9],
    },
    service: '1 service · last at 1,200 km',
  },
  lift: {
    status: 'Examination in date · ready to hire',
    callouts: [
      { k: 'Thorough exam', v: 'In date to Mar' },
      { k: 'Pre-use check', v: 'Done today 7:42' },
      { k: 'Battery', v: '86%' },
      { k: 'Safe working load', v: '450 kg' },
    ],
    live: {
      label: 'Hours this week',
      value: '18.5',
      unit: 'hours',
      sub: '1,240 hours on the meter · on hire',
      series: [0, 3.2, 4.1, 3.8, 0, 0, 2.6, 3.9, 4.4, 0, 3.1, 0, 0, 4.2, 3.6, 2.8, 0, 3.4, 4.0, 3.5],
    },
    service: '2 examinations, both in date',
  },
  battery: {
    status: 'Charging from solar',
    callouts: [
      { k: 'Battery', v: '78%' },
      { k: 'Solar', v: '3.1 kW' },
      { k: 'Home', v: '1.2 kW' },
      { k: 'Backup', v: 'Ready' },
    ],
    live: {
      label: 'Powered from the sun today',
      value: '86',
      unit: '%',
      sub: '14.2 kWh stored · £3.40 saved',
      series: [0, 0, 0.2, 0.8, 1.6, 2.6, 3.4, 3.8, 3.6, 3.1, 2.4, 1.6, 0.9, 0.4, 0.2, 0, 0, 0, 0, 0],
    },
    service: 'Health check · 98% capacity',
  },
  robotmower: {
    status: 'Mowing · 62% done',
    callouts: [
      { k: 'Battery', v: '71%' },
      { k: 'Blades', v: 'Sharp' },
      { k: 'Next cut', v: 'Tomorrow 9:00' },
      { k: 'Rain', v: 'Pauses if wet' },
    ],
    live: {
      label: 'Mowed this week',
      value: '3',
      unit: 'cuts',
      sub: '1,180 m² · cut to 4 cm',
      series: [0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1],
    },
    service: '1 service · blades changed',
  },
  icemaker: {
    status: 'Making ice · bin 80% full',
    callouts: [
      { k: 'Filter', v: 'Due in 3 weeks' },
      { k: 'Last clean', v: '9 Feb' },
      { k: 'Today', v: '146 kg' },
      { k: 'Warranty', v: 'To Apr 2029' },
    ],
    live: {
      label: 'Ice today',
      value: '146',
      unit: 'kg',
      sub: 'Bin 80% full · 0.9 l of water per kg',
      series: [1.2, 1.1, 1.0, 1.0, 1.1, 1.4, 2.0, 2.6, 3.0, 3.2, 3.4, 3.3, 3.0, 2.6, 2.4, 2.8, 3.4, 3.8, 3.6, 3.0],
    },
    service: '2 cleans · last 9 Feb',
  },
  coffee: {
    status: 'Ready · 9 bar',
    callouts: [
      { k: 'Boiler', v: '124°C' },
      { k: 'Shots today', v: '212' },
      { k: 'Filter', v: 'Due in 3 weeks' },
      { k: 'Warranty', v: 'To Apr 2028' },
    ],
    live: {
      label: 'Shots today',
      value: '212',
      unit: 'shots',
      sub: 'Average 27 s · 9.0 bar',
      series: [0, 0.4, 2.6, 3.8, 3.4, 2.4, 1.8, 2.2, 3.0, 3.6, 2.8, 1.6, 1.2, 1.8, 2.2, 1.6, 1.0, 0.6, 0.2, 0],
    },
    service: '2 services · last 16 Feb',
  },
  hottub: {
    status: 'Ready · 38°C',
    callouts: [
      { k: 'Water', v: '38°C' },
      { k: 'Chlorine', v: '4 ppm' },
      { k: 'Filters', v: 'Due in 2 weeks' },
      { k: 'Cover', v: 'On' },
    ],
    live: {
      label: 'Energy this week',
      value: '41',
      unit: 'kWh',
      sub: 'Cover on · 6°C outside',
      series: [6.2, 5.8, 6.0, 6.4, 5.6, 5.2, 7.8, 8.4, 6.0, 5.8, 5.6, 6.2, 6.0, 5.4, 5.2, 5.0, 7.2, 8.0, 6.2, 5.8],
    },
    service: '1 service · filters changed',
  },
};

/** "12 Mar 2026" → "12 Mar". */
const dayMonth = (d: string) => d.split(' ').slice(0, 2).join(' ');

/** The phone values for one unit, with fallbacks built from the unit itself. */
export function phoneData(s: Sector): Required<Omit<PhoneExtra, 'live' | 'heroPos'>> & Pick<PhoneExtra, 'live' | 'heroPos'> {
  const x = EXTRA[s.key];
  const callouts = (x?.callouts ?? [
    { k: 'Warranty', v: 'Active' },
    { k: 'Handed over', v: dayMonth(s.dates.handover) },
    { k: s.partnerRole === 'dealer' ? 'Dealer' : 'Installer', v: s.partnerShort },
    { k: 'Firmware', v: 'Up to date' },
  ]).map((c, i) => ({ ...c, at: c.at ?? ANCHORS[i] })) as PhoneExtra['callouts'];
  return {
    status: x?.status ?? 'Registered · in warranty',
    callouts,
    live: x?.live,
    heroPos: x?.heroPos,
    service: x?.service ?? `Handed over ${dayMonth(s.dates.handover)}`,
    parts: x?.parts ?? `${s.part} · fits this serial`,
  };
}

// ---------- Colour helpers: the maker's accent, lifted so it reads on a dark screen ----------
const hex = (h: string) => {
  const m = h.replace('#', '');
  const n = parseInt(m.length === 3 ? m.replace(/./g, (c) => c + c) : m, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Mix the accent towards white by t (0..1). */
export function lift(accent: string, t: number) {
  const [r, g, b] = hex(accent);
  const m = (c: number) => Math.round(c + (255 - c) * t);
  return `rgb(${m(r)}, ${m(g)}, ${m(b)})`;
}

export function alpha(accent: string, a: number) {
  const [r, g, b] = hex(accent);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
